import { Link } from 'react-router-dom'
import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8 animate-fade-in">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              About <span className="gradient-text">GistMag</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Making the web more accessible with AI-powered alt text, speech, blog metadata, site audits, and indexing tools.
            </p>
          </div>

          {/* Mission */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Mission</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              GistMag was born from a simple belief: every page on the web should be accessible, fast, and easy to understand. 
              We&apos;re on a mission to make alt text generation, text-to-speech conversion, blog metadata creation, site audits, and 
              indexing tools fast, accurate, and effortless for content creators, developers, and businesses of all sizes.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              By combining cutting-edge AI technology with a developer-friendly API, we&apos;re helping thousands of websites become 
              more accessible while improving their SEO and overall site quality. Our Text-to-Speech capabilities enable content creators 
              to make their written content available as audio, our Site Audit tool gives clear scores for performance, accessibility, 
              best practices, and SEO, and our Indexing tools let you check whether your pages are indexed in Google.
            </p>
          </div>

          {/* What We Do */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">What We Do</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">🤖</span>
                  AI-Powered Generation
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Our advanced AI models analyze images and generate accurate, contextually relevant alt text 
                  that describes what's in the image, not just what it looks like.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">♿</span>
                  Accessibility First
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  We're committed to making the web accessible to everyone. Every alt text we generate helps 
                  screen readers provide better experiences for visually impaired users.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">🎙️</span>
                  Text-to-Speech
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Convert text to high-quality speech audio with customizable voices, speeds, and languages. 
                  Perfect for creating audio content, accessibility features, and multilingual applications.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">🔌</span>
                  Developer-Friendly
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Built with developers in mind. Simple API, comprehensive documentation, and integrations 
                  for WordPress, Shopify, Make.com, and browser extensions.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">🔍</span>
                  Indexing Tools
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Check whether your pages are indexed in Google. Bulk-check up to 10,000 URLs
                  at once and export results as CSV for easy reporting.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">🔒</span>
                  Privacy & Security
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Your images are processed securely and never stored. We respect your privacy and handle 
                  your data with the utmost care.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Key Features</h2>
            <ul className="space-y-4">
              {[
                'Multiple alt text modes: Short, Long, and SEO-optimised',
                'Text-to-Speech with 9+ languages and customisable voices',
                'Speech-to-Text transcription for audio files',
                'Site Audit with scores for performance, accessibility, best practices, and SEO',
                'Index Check to verify Google indexing status for up to 10,000 URLs',
                'Batch processing for hundreds of images at once',
                'WordPress, Shopify, and Make.com integrations',
                'Browser extensions for Chrome, Edge, and Firefox',
                'RESTful API for custom integrations',
                'Credit-based pricing – pay only for what you use',
                'Credits never expire – use them anytime',
                'Real-time usage analytics and tracking',
                'Secure API key authentication',
                'Background music support for TTS'
              ].map((feature, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Technology */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Technology</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              GistMag leverages state-of-the-art AI models to understand image content and generate 
              accurate descriptions. For text-to-speech, we use Google Cloud Text-to-Speech API, and for speech-to-text transcription, we use OpenAI's Whisper API, 
              providing high-quality, natural-sounding voices in multiple languages. Our system is 
              built on modern cloud infrastructure, ensuring fast response times and high availability.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              We continuously improve our models based on user feedback and the latest advances in 
              computer vision, natural language processing, and speech synthesis technologies.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Ready to Get Started?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Join thousands of users who are making their websites more accessible with GistMag
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/auth/signup"
                className="px-8 py-3 bg-primary-600 dark:bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition hover-lift"
              >
                Get Started Free
              </Link>
              <Link
                to="/how-to-use"
                className="px-8 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition hover-lift border border-gray-300 dark:border-slate-700"
              >
                Learn How to Use
              </Link>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

